"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { toast } from "react-hot-toast";
import {
  EyeIcon,
  EyeSlashIcon,
  EnvelopeIcon,
  LockClosedIcon,
  UserIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/react/24/outline";
import { useAuthSession } from "@/hooks/useAuthSession";
import { getRedirectUrl } from "@/lib/config";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import Script from "next/script";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void;
          renderButton: (
            element: HTMLElement,
            config: Record<string, unknown>
          ) => void;
          prompt: () => void;
        };
      };
    };
  }
}

type LoginFormData = {
  email: string;
  password: string;
};

const LoginPage: React.FC = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl =
    searchParams.get("callbackUrl") || searchParams.get("redirect");

  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<LoginFormData>>({});
  const [error, setError] = useState("");
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  // Mobile OTP login state
  const [loginMethod, setLoginMethod] = useState<"email" | "mobile">("mobile");
  const [mobileStep, setMobileStep] = useState<"phone" | "otp">("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSentTo, setOtpSentTo] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const { user, isLoading: isAuthLoading } = useAuthSession();

  // Check if user is already logged in
  useEffect(() => {
    if (!isAuthLoading && user) {
      // If there's a callback URL, use it; otherwise use default redirect
      if (callbackUrl) {
        router.push(callbackUrl);
      } else {
        const redirectUrl = getRedirectUrl(user.role);
        router.push(redirectUrl);
      }
    }
  }, [user, isAuthLoading, router, callbackUrl]);

  // Google Sign-In callback
  const handleGoogleCallback = useCallback(
    async (response: { credential: string }) => {
      setIsGoogleLoading(true);
      setError("");

      try {
        const res = await fetch("/api/auth/google", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            credential: response.credential,
            rememberMe,
          }),
          credentials: "include",
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || "Google sign-in failed");
        }

        toast.success(
          data.isNewUser
            ? "Welcome! Account created with Google"
            : "Signed in with Google!"
        );
        setIsRedirecting(true);

        setTimeout(() => {
          window.dispatchEvent(new Event("auth-change"));
        }, 150);

        setTimeout(() => {
          if (callbackUrl) {
            router.push(callbackUrl);
          } else if (data.redirectUrl) {
            router.push(data.redirectUrl);
          } else {
            router.push("/profile");
          }
        }, 1000);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Google sign-in failed"
        );
        toast.error(
          err instanceof Error ? err.message : "Google sign-in failed"
        );
      } finally {
        setIsGoogleLoading(false);
      }
    },
    [callbackUrl, rememberMe, router]
  );

  // Initialize Google Sign-In
  const initializeGoogleSignIn = useCallback(() => {
    if (window.google && googleButtonRef.current) {
      window.google.accounts.id.initialize({
        client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || "",
        callback: handleGoogleCallback,
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: "outline",
        size: "large",
        width: googleButtonRef.current.offsetWidth,
        text: "signin_with",
        shape: "rectangular",
        logo_alignment: "center",
      });
    }
  }, [handleGoogleCallback]);

  const validateForm = () => {
    const newErrors: Partial<LoginFormData> = {};

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    if (!validateForm()) {
      setIsLoading(false);
      toast.error("Please fix the errors below");
      return;
    }

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email.toLowerCase().trim(),
          password: formData.password,
          rememberMe,
        }),
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Login failed");
      }

      // Show success message and set redirecting state
      toast.success("Login successful! Redirecting...");
      setIsRedirecting(true);

      // The server sets the auth_token and user cookies automatically
      // Wait a moment for cookies to be set, then trigger auth change event
      console.log("Login successful, waiting for cookies to be set...");
      setTimeout(() => {
        console.log("Dispatching auth-change event");
        window.dispatchEvent(new Event("auth-change"));
      }, 150);

      console.log("Login response data:", data);

      // Add a small delay to show the success message before redirecting
      setTimeout(() => {
        // Redirect to callback URL if provided, otherwise use response or default
        if (callbackUrl) {
          router.push(callbackUrl);
        } else if (data.redirectUrl) {
          router.push(data.redirectUrl);
        } else {
          router.push("/profile");
        }
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      toast.error(err instanceof Error ? err.message : "Login failed");
    } finally {
      // Don't set loading to false immediately if we're redirecting
      if (!isRedirecting) {
        setIsLoading(false);
      }
    }
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!/^[6-9]\d{9}$/.test(phone.replace(/\D/g, "").replace(/^91/, ""))) {
      setError("Please enter a valid 10-digit Indian mobile number");
      return;
    }
    setOtpLoading(true);
    try {
      const res = await fetch("/api/auth/phone/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, role: "STUDENT" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send OTP");
      setOtpSentTo(data.phone || phone);
      setMobileStep("otp");
      setResendCooldown(30);
      toast.success("OTP sent to your mobile number");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to send OTP";
      setError(msg);
      toast.error(msg);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!/^\d{6}$/.test(otp)) {
      setError("Please enter the 6-digit OTP");
      return;
    }
    setOtpLoading(true);
    try {
      const res = await fetch("/api/auth/phone/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: otpSentTo || phone, otp, role: "STUDENT", mode: "login" }),
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.isNewUser) {
          toast("No account found. Redirecting to register...", { icon: "ℹ️" });
          setTimeout(() => router.push(`/register?phone=${encodeURIComponent(otpSentTo || phone)}`), 800);
          return;
        }
        throw new Error(data.error || "Invalid OTP");
      }
      toast.success("Login successful! Redirecting...");
      setIsRedirecting(true);
      setTimeout(() => window.dispatchEvent(new Event("auth-change")), 150);
      setTimeout(() => {
        if (callbackUrl) router.push(callbackUrl);
        else if (data.redirectUrl) router.push(data.redirectUrl);
        else router.push("/profile");
      }, 1000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Verification failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <>


      {/* Full screen loading overlay when redirecting */}
      {isRedirecting && (
        <div className="fixed inset-0 bg-gradient-to-br from-slate-50 to-white z-50 flex items-center justify-center zoom-container">
          <LoadingSpinner
            fullScreen={false}
            showText={true}
            text="Signing in..."
          />
        </div>
      )}

      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        {/* Content */}
        <div className="relative sm:mx-auto sm:w-full sm:max-w-md zoom-container">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="mt-6 text-center text-2xl font-bold text-gray-900 mt-[80px]">
              Login
            </h2>
            <p className="mt-3 text-lg text-gray-600">
              Sign in to your account
            </p>
          </div>

          {/* Login Form */}
          <div className="bg-white/95 backdrop-blur-lg py-8 px-4 shadow-sm sm:shadow-lg sm:rounded-lg sm:px-10 border border-orange-100/50 sm:border-orange-100 transform transition-all duration-300 sm:hover:shadow-xl">
            {/* Login method tabs */}
            <div className="mb-6 grid grid-cols-2 rounded-lg border border-gray-200 bg-gray-50 p-1">
              <button
                type="button"
                onClick={() => {
                  setLoginMethod("mobile");
                  setError("");
                }}
                className={`flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-colors ${
                  loginMethod === "mobile"
                    ? "bg-white text-orange-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <DevicePhoneMobileIcon className="h-4 w-4" />
                Mobile
              </button>
              <button
                type="button"
                onClick={() => {
                  setLoginMethod("email");
                  setError("");
                }}
                className={`flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-colors ${
                  loginMethod === "email"
                    ? "bg-white text-orange-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <EnvelopeIcon className="h-4 w-4" />
                Email
              </button>
            </div>

            {/* Error Message - Fixed Position */}
            {error && (
              <div className="mb-6 text-red-600 text-sm text-center bg-red-50 p-3 rounded-lg border border-red-200 animate-shake">
                {error}
              </div>
            )}

            {loginMethod === "mobile" && mobileStep === "phone" && (
              <form className="space-y-6" onSubmit={handleSendOtp}>
                <div className="group">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 group-focus-within:text-orange-600 transition-colors">
                    Mobile number
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 text-sm">
                      +91
                    </div>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      required
                      maxLength={10}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      className="block w-full pl-12 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      placeholder="9876543210"
                    />
                  </div>
                  <p className="mt-2 text-xs text-gray-500">We&apos;ll send a 6-digit code via SMS.</p>
                </div>
                <button
                  type="submit"
                  disabled={otpLoading}
                  className="w-full flex justify-center items-center px-4 h-12 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50 transition-colors"
                >
                  {otpLoading ? (
                    <><LoadingSpinner size="sm" showText={false} className="mr-2" /><span>Sending...</span></>
                  ) : (
                    "Send OTP"
                  )}
                </button>
              </form>
            )}

            {loginMethod === "mobile" && mobileStep === "otp" && (
              <form className="space-y-6" onSubmit={handleVerifyOtp}>
                <div>
                  <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                    Enter OTP
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    Sent to {otpSentTo || `+91 ${phone}`}.{" "}
                    <button
                      type="button"
                      onClick={() => { setMobileStep("phone"); setOtp(""); setError(""); }}
                      className="text-orange-600 hover:underline"
                    >
                      Change number
                    </button>
                  </p>
                  <input
                    id="otp"
                    name="otp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="mt-2 block w-full px-3 py-3 text-center tracking-[0.4em] text-lg border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="000000"
                  />
                </div>
                <button
                  type="submit"
                  disabled={otpLoading || isRedirecting}
                  className="w-full flex justify-center items-center px-4 h-12 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50 transition-colors"
                >
                  {otpLoading ? (
                    <><LoadingSpinner size="sm" showText={false} className="mr-2" /><span>Verifying...</span></>
                  ) : (
                    "Verify & Login"
                  )}
                </button>
                <div className="text-center text-sm text-gray-600">
                  Didn&apos;t receive the code?{" "}
                  <button
                    type="button"
                    disabled={resendCooldown > 0 || otpLoading}
                    onClick={() => handleSendOtp(new Event("submit") as unknown as React.FormEvent)}
                    className="text-orange-600 hover:underline disabled:text-gray-400 disabled:no-underline"
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend OTP"}
                  </button>
                </div>
              </form>
            )}

            {loginMethod === "email" && (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Email Field */}
              <div className="group">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 group-focus-within:text-orange-600 transition-colors"
                >
                  Email address
                </label>
                <div className="mt-1 relative rounded-md shadow-sm transition-all duration-200 group-focus-within:ring-2 group-focus-within:ring-orange-500 group-focus-within:ring-opacity-50">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400 group-focus-within:text-orange-500 transition-colors" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className={`block w-full pl-10 pr-3 py-3 border ${errors.email ? "border-red-300" : "border-gray-300"
                      } rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm transition-all duration-200`}
                    placeholder="you@example.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600 animate-shake">
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div className="group">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 group-focus-within:text-orange-600 transition-colors"
                >
                  Password
                </label>
                <div className="mt-1 relative rounded-md shadow-sm transition-all duration-200 group-focus-within:ring-2 group-focus-within:ring-orange-500 group-focus-within:ring-opacity-50">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-5 w-5 text-gray-400 group-focus-within:text-orange-500 transition-colors" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    className={`block w-full pl-10 pr-10 py-3 border ${errors.password ? "border-red-300" : "border-gray-300"
                      } rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm transition-all duration-200`}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-gray-400 hover:text-orange-500 focus:outline-none transition-colors"
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600 animate-shake">
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Remember Me + Forgot Password Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label
                    htmlFor="remember-me"
                    className="ml-2 block text-sm text-gray-700 cursor-pointer select-none"
                  >
                    Remember me
                  </label>
                </div>
                <Link
                  href="/forgot-password"
                  className="text-sm text-orange-600 hover:text-orange-500 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <div className="h-12">
                <button
                  type="submit"
                  disabled={isLoading || isRedirecting}
                  className="w-full flex justify-center items-center px-4 h-12 min-h-12 max-h-12 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 overflow-hidden"
                  style={{
                    height: "48px",
                    minHeight: "48px",
                    maxHeight: "48px",
                  }}
                >
                  {isRedirecting ? (
                    <>
                      <LoadingSpinner
                        size="sm"
                        showText={false}
                        className="mr-2"
                      />
                      <span>Redirecting...</span>
                    </>
                  ) : isLoading ? (
                    <>
                      <LoadingSpinner
                        size="sm"
                        showText={false}
                        className="mr-2"
                      />
                      <span>Signing in...</span>
                    </>
                  ) : (
                    "Sign in"
                  )}
                </button>
              </div>
            </form>
            )}



            {/* Divider */}
            <div className="mt-8">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">
                    Don&apos;t have an account?
                  </span>
                </div>
              </div>

              {/* Register Links */}
              <div className="mt-6 space-y-3">
                <Link
                  href="/register"
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all duration-200 hover:border-orange-300 hover:text-orange-600"
                >
                  <UserIcon className="h-5 w-5" />
                  Register as Student
                </Link>
                <Link
                  href="/instructors/register"
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 border border-orange-200 rounded-lg shadow-sm text-sm font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all duration-200 hover:border-orange-300"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  Register as Instructor
                </Link>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-gray-600">
            <p>
              © {new Date().getFullYear()} Relaxing Routine. All rights
              reserved.
            </p>
          </div>
        </div>

        {/* Add custom styles for animations */}
        <style jsx global>{`
          @keyframes shake {
            0%,
            100% {
              transform: translateX(0);
            }
            25% {
              transform: translateX(-5px);
            }
            75% {
              transform: translateX(5px);
            }
          }
          .animate-shake {
            animation: shake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
          }
        `}</style>
      </div>
    </>
  );
};

export default LoginPage;
